import { DEV } from 'cc/env';
import { CCInteger, CurveRange, Game, game, Node, Tween, tween, v3, Vec3, _decorator } from 'cc';
import BaseMovingEffect, { BaseMovingEffectOption } from './BaseMovingEffect';

const { ccclass, property } = _decorator;

export type TweenMovingEffectOption = {
    enableScaleTween?: boolean;
    initScale?: Vec3;
    targetScale?: Vec3;
    onlyUpdateX?: boolean;
    onlyUpdateY?: boolean;
    xScaleCurve?: CurveRange;
    yScaleCurve?: CurveRange;
    useLocalSpace?: boolean;
} & BaseMovingEffectOption;

@ccclass('TweenMovingEffectConfig')
export class TweenMovingEffectConfig {
    @property({ type: CCInteger, displayName: DEV && '表演時長', tooltip: DEV && '表演時長 (單位: 毫秒)' })
    public readonly duration: number = 0;
    @property({ type: CurveRange, tooltip: DEV && 'x 表演曲線' })
    public readonly xAxisCurve: CurveRange = new CurveRange();
    @property({ type: CurveRange, tooltip: DEV && 'y 表演曲線' })
    public readonly yAxisCurve: CurveRange = new CurveRange();
    @property({ displayName: DEV && '啟用縮放變化', tooltip: DEV && '啟用時, Scale也會同步調整' })
    public readonly enableScaleTween: boolean = false;
    @property({ type: CurveRange, tooltip: DEV && 'ScaleX 表演曲線' })
    public readonly xScaleCurve: CurveRange = new CurveRange();
    @property({ type: CurveRange, tooltip: DEV && 'ScaleY 表演曲線' })
    public readonly yScaleCurve: CurveRange = new CurveRange();
}

@ccclass('TweenMovingEffect')
export default class TweenMovingEffect extends BaseMovingEffect {
    @property({ displayName: DEV && '啟用縮放變化', tooltip: DEV && '啟用時, Scale也會同步調整' })
    public readonly enableScaleTween: boolean = false;
    @property({
        type: CurveRange,
        tooltip: DEV && 'ScaleX 表演曲線',
        visible(this: TweenMovingEffect) {
            return this.enableScaleTween;
        },
    })
    public xScaleCurve: CurveRange = new CurveRange();
    @property({
        type: CurveRange,
        tooltip: DEV && 'ScaleY 表演曲線',
        visible(this: TweenMovingEffect) {
            return this.enableScaleTween;
        },
    })
    public yScaleCurve: CurveRange = new CurveRange();
    @property({
        displayName: DEV && '是否只更新X座標',
        visible(this: TweenMovingEffect) {
            return !this.onlyUpdateY;
        },
    })
    public readonly onlyUpdateX: boolean = false;
    @property({
        displayName: DEV && '是否只更新Y座標',
        visible(this: TweenMovingEffect) {
            return !this.onlyUpdateX;
        },
    })
    public readonly onlyUpdateY: boolean = false;
    @property({ displayName: DEV && '使用本地座標空間' })
    public readonly useLocalSpace: boolean = false;
    @property({ type: CurveRange, displayName: DEV && 'x 表演曲線' })
    public xAxisCurve: CurveRange = new CurveRange();
    @property({ type: CurveRange, displayName: DEV && 'y 表演曲線' })
    public yAxisCurve: CurveRange = new CurveRange();
    @property({ type: Node, formerlySerializedAs: 'from' })
    private _from: Node = null;
    @property({ type: Node, formerlySerializedAs: 'to' })
    private _to: Node = null;
    @property
    private _useInitScale: boolean = false;
    @property
    private _fromScale: Vec3 = v3(0, 0, 1);
    @property
    private _toScale: Vec3 = v3(1, 1, 1);
    @property({ type: CCInteger, formerlySerializedAs: 'duration' })
    private _duration: number = 0;

    @property({ displayName: DEV && '是否隨機表演曲線' })
    private readonly useRandom: boolean = false;
    @property({
        type: [TweenMovingEffectConfig],
        visible(this: TweenMovingEffect) {
            return this.useRandom;
        },
        displayName: DEV && '隨機表演曲線設定組',
    })
    private readonly randomFlyingConfig: TweenMovingEffectConfig[] = [];
    @property({ tooltip: DEV && '使該節點不受全域TimeScale影響' })
    private readonly notEffectedByGlobalTimeScale: boolean = false;

    private _randomRatio: number = 1;
    private _currentTween: Tween<Node> = null;

    @property({ type: Node, displayName: DEV && '起點' })
    public get from() {
        return this._from;
    }
    public set from(value: Node) {
        if (this._from === value) {
            return;
        }
        this._from = value;
    }

    @property({ type: Node, displayName: DEV && '終點' })
    public get to() {
        return this._to;
    }
    public set to(value: Node) {
        if (this._to === value) {
            return;
        }
        this._to = value;
    }

    @property({ displayName: DEV && '使用起始縮放' })
    public get useInitScale() {
        return this._useInitScale;
    }
    public set useInitScale(value: boolean) {
        if (this._useInitScale === value) {
            return;
        }
        this._useInitScale = value;
    }

    @property({
        type: Vec3,
        displayName: DEV && '起始縮放',
        visible(this: TweenMovingEffect) {
            return this.enableScaleTween && this.useInitScale;
        },
    })
    public get fromScale() {
        return this._fromScale;
    }
    public set fromScale(value: Vec3) {
        if (this._fromScale.equals(value)) {
            return;
        }
        this._fromScale = value;
    }

    @property({
        type: Vec3,
        displayName: DEV && '結束縮放',
        visible(this: TweenMovingEffect) {
            return this.enableScaleTween;
        },
    })
    public get toScale() {
        return this._toScale;
    }
    public set toScale(value: Vec3) {
        if (this._toScale.equals(value)) {
            return;
        }
        this._toScale = value;
    }

    @property({ type: CCInteger, displayName: DEV && '表演時長', tooltip: DEV && '表演時長 (單位: 毫秒)' })
    public get duration() {
        return this._duration;
    }
    public set duration(value: number) {
        if (this._duration === value) {
            return;
        }
        this._duration = value;
    }

    private get config() {
        if (this.useRandom) {
            const randomIndex = Math.floor(Math.random() * this.randomFlyingConfig.length);
            if (randomIndex < this.randomFlyingConfig.length) {
                return this.randomFlyingConfig[randomIndex];
            }
        }
        return {
            xAxisCurve: this.xAxisCurve,
            yAxisCurve: this.yAxisCurve,
            duration: this.duration,
            enableScaleTween: this.enableScaleTween,
            xScaleCurve: this.xScaleCurve,
            yScaleCurve: this.yScaleCurve,
        };
    }

    public play(option?: TweenMovingEffectOption) {
        return super.play(option);
    }

    public async playWithoutProcessor(option?: TweenMovingEffectOption) {
        option = this._parseOption(option);
        await this._play(option);
    }

    protected onLoad() {
        if (this.notEffectedByGlobalTimeScale) {
            game.on(Game.EVENT_TIME_SCALE_CHANGED, () => {
                if (this._currentTween) {
                    this._currentTween.timeScale(1 / game.timeScale);
                }
            });
        }
    }

    protected *run(option?: TweenMovingEffectOption) {
        yield this.playWithoutProcessor(option);
    }

    protected async finish(isEndNaturely: boolean) {
        if (!isEndNaturely) {
            this._currentTween.setTime(Number.MAX_SAFE_INTEGER);
            this._currentTween.stop();
        }
    }

    private _parseOption(option: TweenMovingEffectOption) {
        const useLocalSpace = option?.useLocalSpace ?? this.useLocalSpace;
        return {
            target: option?.target ?? this.node,
            fromPos: option?.fromPos ?? (useLocalSpace ? this._from?.position : this._from?.worldPosition),
            toPos: option?.toPos ?? (useLocalSpace ? this._to?.position : this._to?.worldPosition),
            startAt: option?.startAt ?? 0,
            overrideDuration: option?.overrideDuration,
            enableScaleTween: option?.enableScaleTween ?? this.enableScaleTween,
            initScale: option?.initScale ?? this.fromScale,
            targetScale: option?.targetScale ?? this.toScale,
            xScaleCurve: option?.xScaleCurve ?? this.xScaleCurve,
            yScaleCurve: option?.yScaleCurve ?? this.yScaleCurve,
            onlyUpdateX: option?.onlyUpdateX ?? this.onlyUpdateX,
            onlyUpdateY: option?.onlyUpdateY ?? this.onlyUpdateY,
            useLocalSpace,
        };
    }

    private _play({
        target,
        fromPos,
        toPos,
        enableScaleTween,
        initScale,
        xScaleCurve,
        yScaleCurve,
        targetScale,
        overrideDuration,
        startAt,
        onlyUpdateX,
        onlyUpdateY,
        useLocalSpace,
    }: TweenMovingEffectOption) {
        const { xAxisCurve, yAxisCurve, duration } = this.config;
        const actualDuration = (overrideDuration ?? duration) / 1000;
        this._randomRatio = Math.random();
        if (useLocalSpace) {
            target.setPosition(fromPos);
        } else {
            target.setWorldPosition(fromPos);
            toPos = target.parent.transform.convertToNodeSpaceAR(toPos);
        }
        if (enableScaleTween && this.useInitScale) {
            target.setScale(initScale);
        }
        this._currentTween = tween(target).parallel(
            !onlyUpdateY
                ? tween().to(
                      actualDuration,
                      {
                          x: toPos.x,
                      },
                      {
                          easing: (t) => xAxisCurve.evaluate(t, this._randomRatio),
                      },
                  )
                : tween(),
            !onlyUpdateX
                ? tween().to(
                      actualDuration,
                      {
                          y: toPos.y,
                      },
                      {
                          easing: (t) => yAxisCurve.evaluate(t, this._randomRatio),
                      },
                  )
                : tween(),
            enableScaleTween
                ? tween().to(
                      actualDuration,
                      {
                          scaleX: targetScale.x,
                      },
                      {
                          easing: (t) => xScaleCurve.evaluate(t, this._randomRatio),
                      },
                  )
                : tween(),
            enableScaleTween
                ? tween().to(
                      actualDuration,
                      {
                          scaleY: targetScale.y,
                      },
                      {
                          easing: (t) => yScaleCurve.evaluate(t, this._randomRatio),
                      },
                  )
                : tween(),
        );
        return this._currentTween.promisifyStart(startAt / 1000);
    }
}
